import { Navigate } from 'react-router-dom';
import type { RouteHandleObject, ThmRouteObject } from 'src/common/interfaces';
import { CompanyFeatureName } from 'src/common/utils/company-features';
import { routeProtector } from 'src/router/route-protector';
import { isBusinessUserOrCoporatePartner, userIsPartOfAWorkspace } from '../route-loaders';
const handle: Partial<RouteHandleObject> = {
  isFullWidth: true
};
export const managementDashboardRoutes: ThmRouteObject[] = [{
  path: '/client/:companyId/',
  loader: routeProtector().requireCorporatePartner().requireCompletedOnboardingAndWelcome(),
  async lazy() {
    const {
      ManagementDashboard
    } = await import('src/features/management-dashboard');
    return {
      Component: ManagementDashboard
    };
  },
  children: [{
    path: 'dashboard',
    handle: {
      ...handle,
      name: 'mgt_dash_dashboard'
    },
    loader: routeProtector().requireFeatureFlag('mgt-dashboard-v3'),
    async lazy() {
      const {
        Dashboard
      } = await import('src/features/management-dashboard/dashboard');
      return {
        Component: Dashboard
      };
    }
  }, {
    path: 'product',
    handle: {
      ...handle,
      name: 'mgt_dash_product'
    },
    loader: routeProtector().requireFeatureFlag('mgt-dashboard-v3').requireFeatureEnabled(CompanyFeatureName.SOC_SIM).requireFeatureEnabled(CompanyFeatureName.THREAT_HUNTING).requireFeatureEnabled(CompanyFeatureName.TTX),
    async lazy() {
      const {
        Product
      } = await import('src/features/management-dashboard/v3/product');
      return {
        Component: Product
      };
    }
  }, {
    path: '',
    loader: routeProtector().requireCorporatePartner(),
    async lazy() {
      const {
        Users
      } = await import('src/features/management-dashboard');
      return {
        Component: Users
      };
    },
    children: [{
      path: '',
      element: <Navigate to="./users" />
    }, {
      path: 'users',
      handle: {
        name: 'mgt_dash_users'
      },
      async lazy() {
        const {
          UserManagementView
        } = await import('src/features/management-dashboard/users/views/user-management');
        return {
          Component: UserManagementView
        };
      }
    }, {
      path: 'admins',
      handle: {
        name: 'mgt_dash_admins'
      },
      async lazy() {
        const {
          UserManagementAdminsView
        } = await import('src/features/management-dashboard');
        return {
          Component: UserManagementAdminsView
        };
      }
    }, {
      path: 'groups',
      handle: {
        name: 'mgt_dash_groups'
      },
      async lazy() {
        const {
          UserManagementGroupsView
        } = await import('src/features/management-dashboard');
        return {
          Component: UserManagementGroupsView
        };
      }
    }]
  }, {
    path: 'guides',
    handle: {
      name: 'mgt_dash_guides'
    },
    loader: routeProtector().requireCorporatePartner(),
    async lazy() {
      const {
        Guides
      } = await import('src/features/management-dashboard');
      return {
        Component: Guides
      };
    }
  }, {
    path: 'certifications',
    handle: {
      name: 'mgt_dash_certifications'
    },
    loader: routeProtector().requireCorporatePartner().requireFeatureFlag('business-management-dashboard-certs').requireFeatureEnabled(CompanyFeatureName.CERTIFICATIONS),
    async lazy() {
      const {
        Certifications
      } = await import('src/features/management-dashboard');
      return {
        Component: Certifications
      };
    }
  }, {
    path: 'reports',
    async lazy() {
      const {
        Reports
      } = await import('src/features/management-dashboard');
      return {
        Component: Reports
      };
    },
    children: [{
      path: '',
      element: <Navigate to="./content" />
    }, {
      path: 'content',
      handle: {
        name: 'mgt_dash_reports_content'
      },
      async lazy() {
        const {
          ReportsContentView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsContentView
        };
      }
    }, {
      path: 'assignments',
      handle: {
        name: 'mgt_dash_reports_assignments'
      },
      async lazy() {
        const {
          ReportsAssignmentsView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsAssignmentsView
        };
      }
    }, {
      path: 'time',
      handle: {
        name: 'mgt_dash_reports_time'
      },
      async lazy() {
        const {
          ReportsTimeView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsTimeView
        };
      }
    }, {
      path: 'activity',
      handle: {
        name: 'mgt_dash_reports_activity'
      },
      async lazy() {
        const {
          ReportsActivityView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsActivityView
        };
      }
    }, {
      path: 'time-content',
      handle: {
        name: 'mgt_dash_reports_time_content'
      },
      async lazy() {
        const {
          ReportsTimeContentView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsTimeContentView
        };
      }
    }, {
      path: 'skills-matrix',
      handle: {
        name: 'mgt_dash_reports_skills_matrix'
      },
      async lazy() {
        const {
          ReportsSkillsMatrixView
        } = await import('src/features/management-dashboard');
        return {
          Component: ReportsSkillsMatrixView
        };
      }
    }]
  }, {
    path: 'soc-sim',
    loader: routeProtector().requireBusinessUserOrCorporatePartner().requireFeatureEnabled(CompanyFeatureName.SOC_SIM),
    async lazy() {
      const {
        SocSim
      } = await import('src/features/management-dashboard');
      return {
        Component: SocSim
      };
    },
    children: [{
      path: '',
      element: <Navigate to="./reports/team" />
    }, {
      path: 'reports/team',
      handle: {
        name: 'mgt_dash_soc_sim_team_report'
      },
      async lazy() {
        const {
          TeamReportView
        } = await import('src/features/management-dashboard/soc-sim');
        return {
          Component: TeamReportView
        };
      }
    }, {
      path: 'reports/users',
      children: [{
        path: '',
        handle: {
          name: 'mgt_dash_soc_sim_user_reports'
        },
        async lazy() {
          const {
            UserReportsView
          } = await import('src/features/management-dashboard/soc-sim');
          return {
            Component: UserReportsView
          };
        }
      }, {
        path: ':userId',
        handle: {
          name: 'mgt_dash_soc_sim_user_report'
        },
        async lazy() {
          const {
            UserReportView
          } = await import('src/features/management-dashboard/soc-sim');
          return {
            Component: UserReportView
          };
        }
      }]
    }]
  }, {
    path: 'threat-hunting-simulator',
    loader: routeProtector().requireBusinessUserOrCorporatePartner().requireFeatureEnabled(CompanyFeatureName.THREAT_HUNTING),
    async lazy() {
      const {
        ThreatHunting
      } = await import('src/features/management-dashboard');
      return {
        Component: ThreatHunting
      };
    },
    children: [{
      path: '',
      element: <Navigate to="./reports/team" />
    }, {
      path: 'reports/team',
      handle: {
        name: 'mgt_dash_threat_hunting_team_report'
      },
      async lazy() {
        const {
          ThreatHuntingTeamReportView
        } = await import('src/features/management-dashboard/threat-hunting');
        return {
          Component: ThreatHuntingTeamReportView
        };
      }
    }, {
      path: 'reports/users',
      children: [{
        path: '',
        handle: {
          name: 'mgt_dash_threat_hunting_user_reports'
        },
        async lazy() {
          const {
            ThreatHuntingUserReportsView
          } = await import('src/features/management-dashboard/threat-hunting');
          return {
            Component: ThreatHuntingUserReportsView
          };
        }
      }, {
        path: ':userId',
        handle: {
          name: 'mgt_dash_threat_hunting_user_report'
        },
        async lazy() {
          const {
            ThreatHuntingUserReportView
          } = await import('src/features/management-dashboard/threat-hunting');
          return {
            Component: ThreatHuntingUserReportView
          };
        }
      }]
    }]
  }, {
    path: 'paths',
    loader: routeProtector().requireCorporatePartner().requireFeatureEnabled(CompanyFeatureName.PATHS),
    async lazy() {
      const {
        LearningPaths
      } = await import('src/features/management-dashboard');
      return {
        Component: LearningPaths
      };
    },
    children: [{
      path: '',
      element: <Navigate to="../content-studio" />
    }, {
      path: 'modules',
      handle: {
        name: 'mgt_dash_modules'
      },
      async lazy() {
        const {
          ModulesView
        } = await import('src/features/management-dashboard');
        return {
          Component: ModulesView
        };
      }
    }, {
      path: 'modules/new',
      handle: {
        name: 'mgt_dash_modules_new'
      },
      async lazy() {
        const {
          CreateModuleView
        } = await import('src/features/management-dashboard');
        return {
          Component: CreateModuleView
        };
      }
    }, {
      path: 'modules/:moduleId',
      handle: {
        name: 'mgt_dash_modules_edit'
      },
      async lazy() {
        const {
          EditModuleView
        } = await import('src/features/management-dashboard');
        return {
          Component: EditModuleView
        };
      }
    }, {
      path: 'new',
      handle: {
        name: 'mgt_dash_paths_new'
      },
      async lazy() {
        const {
          CreatePathView
        } = await import('src/features/management-dashboard');
        return {
          Component: CreatePathView
        };
      }
    }, {
      path: ':pathCode',
      handle: {
        name: 'mgt_dash_paths_details'
      },
      async lazy() {
        const {
          PathDetails
        } = await import('src/features/management-dashboard');
        return {
          Component: PathDetails
        };
      }
    }, {
      path: ':pathCode/manage',
      handle: {
        name: 'mgt_dash_paths_manage'
      },
      async lazy() {
        const {
          ManagePathView
        } = await import('src/features/management-dashboard');
        return {
          Component: ManagePathView
        };
      }
    }]
  }, {
    path: 'content-studio',
    handle: {
      name: 'mgt_dash_content_studio'
    },
    loader: routeProtector().requireCorporatePartner().requireFeatureEnabled(CompanyFeatureName.CONTENT_STUDIO),
    async lazy() {
      const {
        ContentStudio
      } = await import('src/features/management-dashboard');
      return {
        Component: ContentStudio
      };
    }
  }, {
    path: 'assignments',
    handle: {
      name: 'mgt_dash_assignments'
    },
    loader: routeProtector().requireCorporatePartner().requireFeatureEnabled(CompanyFeatureName.ASSIGNMENTS),
    async lazy() {
      const {
        Assignments
      } = await import('src/features/management-dashboard');
      return {
        Component: Assignments
      };
    }
  }, {
    path: 'create-assignment/:contentId',
    handle: {
      name: 'mgt_dash_create_assignment'
    },
    loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.ASSIGNMENTS),
    async lazy() {
      const {
        CreateAssignment
      } = await import('src/features/management-dashboard');
      return {
        Component: CreateAssignment
      };
    }
  }, {
    path: 'assignments/:assignmentId',
    handle: {
      name: 'mgt_dash_assignment_details'
    },
    loader: routeProtector().requireCorporatePartner().requireFeatureEnabled(CompanyFeatureName.ASSIGNMENTS),
    async lazy() {
      const {
        AssignmentDetails
      } = await import('src/features/management-dashboard');
      return {
        Component: AssignmentDetails
      };
    }
  }, {
    path: 'settings',
    loader: routeProtector().requireCorporatePartner(),
    async lazy() {
      const {
        ManagementDashboardSettings
      } = await import('src/features/management-dashboard');
      return {
        Component: ManagementDashboardSettings
      };
    },
    children: [{
      path: '',
      element: <Navigate to="./room-branding" />
    }, {
      path: 'api',
      handle: {
        name: 'mgt_dash_settings_api'
      },
      loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.API_ACCESS),
      async lazy() {
        const {
          SettingsApiView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsApiView
        };
      }
    }, {
      path: 'billing',
      handle: {
        name: 'mgt_dash_settings_billing'
      },
      async lazy() {
        const {
          SettingsBillingView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsBillingView
        };
      }
    }, {
      path: 'room-branding',
      handle: {
        name: 'mgt_dash_settings_room_branding'
      },
      async lazy() {
        const {
          SettingsGeneralView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsGeneralView
        };
      }
    }, {
      path: 'sso',
      handle: {
        name: 'mgt_dash_settings_sso'
      },
      loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.SSO),
      async lazy() {
        const {
          SettingsSSOView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsSSOView
        };
      }
    }, {
      path: 'preferences',
      handle: {
        name: 'mgt_dash_settings_preferences'
      },
      loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.DISABLE_VM_INTERNET),
      async lazy() {
        const {
          SettingsPreferencesView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsPreferencesView
        };
      }
    }, {
      path: 'reminders',
      handle: {
        name: 'mgt_dash_settings_reminders'
      },
      async lazy() {
        const {
          SettingsRemindersView
        } = await import('src/features/management-dashboard');
        return {
          Component: SettingsRemindersView
        };
      }
    }]
  }, {
    path: 'MITRE',
    loader: routeProtector().requireFeatureFlag('mitre-page').requireCorporatePartner().requireFeatureEnabled(CompanyFeatureName.MITRE),
    handle: {
      name: 'mgt_dash_mitre'
    },
    async lazy() {
      const {
        Mitre
      } = await import('src/features/management-dashboard/mitre/mitre');
      return {
        Component: Mitre
      };
    }
  }, {
    path: 'billing',
    element: <Navigate to="../settings/billing" />
  }, {
    path: 'api',
    loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.API_ACCESS),
    element: <Navigate to="../settings/api" />
  }, {
    path: 'sso',
    loader: routeProtector().requireFeatureEnabled(CompanyFeatureName.SSO),
    element: <Navigate to="../settings/sso" />
  }]
}, {
  path: '/client/:companyId/users/:userId',
  loader: routeProtector().requireCorporatePartner().requireCompletedOnboardingAndWelcome(),
  async lazy() {
    const {
      CompanyUserDetailsView
    } = await import('src/features/management-dashboard');
    return {
      Component: CompanyUserDetailsView
    };
  }
}, {
  path: '/workspace',
  loader: routeProtector().requireLogin(),
  children: [{
    path: '',
    async lazy() {
      const {
        Workspace
      } = await import('src/features/management-dashboard');
      return {
        Component: Workspace
      };
    }
  }, {
    path: 'create',
    loader: routeProtector().requireFeatureFlag('v2-workspaces'),
    async lazy() {
      const {
        CreateWorkspace
      } = await import('src/features/management-dashboard/workspace/create-workspace/create-workspace');
      return {
        Component: CreateWorkspace
      };
    }
  }]
}, {
  path: '/ctf-builder',
  loader: routeProtector().requireOneOf({
    redirectTo: '/workspace?ref=ctf-builder',
    loaders: [userIsPartOfAWorkspace, isBusinessUserOrCoporatePartner]
  }),
  children: [{
    path: '',
    async lazy() {
      const {
        CTFBuilderV2
      } = await import('src/features/management-dashboard/ctf-builder/ctf-builder-v2');
      return {
        Component: CTFBuilderV2
      };
    },
    handle: {
      isHeaderTransparent: true,
      isHeaderSticky: false
    }
  }, {
    path: ':moduleUrl',
    loader: routeProtector().requireFeatureFlag('ctf-builder-v3'),
    async lazy() {
      const {
        CtfEditEvent
      } = await import('src/features/management-dashboard/ctf-builder/ctf-edit-event/ctf-edit-event');
      return {
        Component: CtfEditEvent
      };
    }
  }]
}, {
  path: '/client/:companyId/set-up/',
  loader: routeProtector().requireCorporatePartner().requireFeatureFlag('self-onboarding-admins'),
  async lazy() {
    const {
      Setup
    } = await import('src/features/management-dashboard/setup');
    return {
      Component: Setup
    };
  },
  handle: {
    isFullWidth: true,
    name: 'mgt_dash_setup',
    hideFooter: true,
    hideHeader: true
  },
  children: [{
    path: 'welcome',
    async lazy() {
      const {
        Welcome
      } = await import('src/features/management-dashboard/setup/views/welcome');
      return {
        Component: Welcome
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup_welcome',
      hideFooter: true,
      hideHeader: true
    }
  }, {
    path: 'step-1',
    async lazy() {
      const {
        Step1
      } = await import('src/features/management-dashboard/setup/views/step-1');
      return {
        Component: Step1
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup_1',
      hideFooter: true,
      hideHeader: true
    }
  }, {
    path: 'step-2',
    async lazy() {
      const {
        Step2
      } = await import('src/features/management-dashboard/setup/views/step-2');
      return {
        Component: Step2
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup_2',
      hideFooter: true,
      hideHeader: true
    }
  }, {
    path: 'step-3',
    async lazy() {
      const {
        Step3
      } = await import('src/features/management-dashboard/setup/views/step-3');
      return {
        Component: Step3
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup_3',
      hideFooter: true,
      hideHeader: true
    }
  }, {
    path: 'step-4',
    async lazy() {
      const {
        Step4
      } = await import('src/features/management-dashboard/setup/views/step-4');
      return {
        Component: Step4
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup_4',
      hideFooter: true,
      hideHeader: true
    }
  }, {
    path: 'completed',
    async lazy() {
      const {
        SetUpCompleted
      } = await import('src/features/management-dashboard/setup/views/completed');
      return {
        Component: SetUpCompleted
      };
    },
    handle: {
      isFullWidth: true,
      name: 'mgt_dash_setup-completed',
      hideFooter: true,
      hideHeader: true
    }
  }]
}];